import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  Spinner,
  mergeClasses,
} from "@fluentui/react-components";
import { DismissRegular } from "@fluentui/react-icons";
import { DatePicker } from "@fluentui/react-datepicker-compat";
import { FieldError } from "components/fieldError/FieldError";
import { IFeedback } from "models/IFeedback";
import { useState } from "react";
import {
  useSharedButtonStyles,
  useSharedDialogFooterStyles,
} from "theme/sharedStyles";
import { downloadCSV, generateCSV, stripHtmlTags } from "utils/generalUtils";
import { useExportDialogStyles } from "./ExportToExcelDialog.styles";
import { IExportToExcelDialogProps } from "./ExportToExcelDialog.types";

const FEEDBACK_CSV_COLUMNS: {
  header: string;
  accessor: (item: IFeedback) => string;
}[] = [
  { header: "Feedback Type", accessor: (f) => f.feedbackType },
  { header: "Content", accessor: (f) => stripHtmlTags(f.content) },
  { header: "Prompt", accessor: (f) => f.prompt },
  { header: "Response", accessor: (f) => f.response },
  { header: "LLM Model", accessor: (f) => f.llm },
  { header: "NHQA Enabled", accessor: (f) => (f.nhqaEnabled ? "Yes" : "No") },
  { header: "Has Files", accessor: (f) => (f.hasFiles ? "Yes" : "No") },
  {
    header: "Has Knowledge Components",
    accessor: (f) => (f.hasKnowledgeComponents ? "Yes" : "No"),
  },
  {
    header: "Request Assistance",
    accessor: (f) => (f.requestAssistance ? "Yes" : "No"),
  },
  { header: "Author", accessor: (f) => f.user.displayName },
  {
    header: "Submitted At",
    accessor: (f) => new Date(f.submittedAt * 1000).toISOString(),
  },
];

export const ExportToExcelDialog = (
  props: IExportToExcelDialogProps,
): JSX.Element => {
  const styles = useExportDialogStyles();
  const footerStyles = useSharedDialogFooterStyles();
  const buttonStyles = useSharedButtonStyles();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);

  const handleExport = async () => {
    const errors: Record<string, string> = {};

    if (!startDate) {
      errors.startDate = "Start Date is required";
    }
    if (!endDate) {
      errors.endDate = "End Date is required";
    }
    if (startDate && endDate && startDate > endDate) {
      errors.endDate = "End Date cannot be before Start Date";
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setFieldErrors({});
    setIsLoading(true);

    try {
      const adjustedStartDate = new Date(startDate!);
      adjustedStartDate.setHours(0, 0, 0, 0);
      const startTimestamp = Math.floor(adjustedStartDate.getTime() / 1000);

      const adjustedEndDate = new Date(endDate!);
      adjustedEndDate.setHours(23, 59, 59, 999);
      const endTimestamp = Math.floor(adjustedEndDate.getTime() / 1000);

      const filteredData = props.feedback.filter((feedback) => {
        const feedbackTimestamp = feedback.submittedAt;
        return (
          feedbackTimestamp >= startTimestamp &&
          feedbackTimestamp <= endTimestamp
        );
      });

      if (filteredData.length === 0) {
        setFieldErrors({
          general: "No data found for the selected date range.",
        });
        setIsLoading(false);
        return;
      }

      const csv = generateCSV(filteredData, FEEDBACK_CSV_COLUMNS);
      downloadCSV(csv, "Feedback_Export.csv");
      props.onDismiss(true);
    } catch (error) {
      setFieldErrors({ general: "An error occurred while exporting." });
      console.error("Export Error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={props.isOpen} modalType="alert">
      <DialogSurface
        className={mergeClasses(
          styles.dialogSurface,
          footerStyles.dialogSurface,
        )}
        aria-describedby={undefined}
      >
        <DialogBody>
          <DialogTitle
            action={
              <Button
                appearance="subtle"
                icon={<DismissRegular />}
                aria-label="Close dialog"
                onClick={() => props.onDismiss(false)}
              />
            }
          >
            Export
          </DialogTitle>

          <DialogContent className={styles.dialogContent}>
            {isLoading ? (
              <Spinner
                label="Exporting feedback..."
                className={footerStyles.dialogSpinner}
              />
            ) : (
              <>
                <Field
                  label="Start Date"
                  required
                  validationState={fieldErrors.startDate ? "error" : "none"}
                  validationMessage={fieldErrors.startDate}
                >
                  <DatePicker
                    placeholder="Select Start Date"
                    value={startDate}
                    onSelectDate={(date: Date | null | undefined) => {
                      setStartDate(date ?? null);
                      if (fieldErrors.startDate) {
                        setFieldErrors((prev) => ({ ...prev, startDate: "" }));
                      }
                    }}
                  />
                </Field>

                <Field
                  label="End Date"
                  required
                  validationState={fieldErrors.endDate ? "error" : "none"}
                  validationMessage={fieldErrors.endDate}
                >
                  <DatePicker
                    placeholder="Select End Date"
                    value={endDate}
                    onSelectDate={(date: Date | null | undefined) => {
                      setEndDate(date ?? null);
                      if (fieldErrors.endDate) {
                        setFieldErrors((prev) => ({ ...prev, endDate: "" }));
                      }
                    }}
                  />
                </Field>

                {fieldErrors.general && (
                  <FieldError message={fieldErrors.general} />
                )}
              </>
            )}
          </DialogContent>

          <DialogActions className={styles.dialogActions}>
            <Button
              appearance="primary"
              onClick={handleExport}
              disabled={isLoading}
              className={buttonStyles.primaryButton}
            >
              Export
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
